/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars, no-await-in-loop */
import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip, Box, Alert } from '@mui/material';
import { logger } from '@musedrops/shared';
import { TagApi } from '../../services/tagApi';

interface TagsInputProps {
  value: string | null | undefined; // Comma-separated string or null
  onChange: (tags: string | null) => void;
  label?: string;
  fullWidth?: boolean;
}

/**
 * TagsInput component for shows and stories
 * Uses Autocomplete with multi-select to manage tags
 * Stores tags as comma-separated string, auto-creates new tags
 */
export const TagsInput: React.FC<TagsInputProps> = ({
  value,
  onChange,
  label = 'Tags',
  fullWidth = true,
}) => {
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Convert comma-separated string to array
  const selectedTags = value ? value.split(',').filter(t => t.trim()) : [];

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const tags = await TagApi.getAll();
      setAllTags(tags.map(t => t.tag));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tags');
      logger.error('Failed to load tags', { error: err });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (_event: unknown, newValue: string[]) => {
    try {
      // Normalize all tags to kebab-case
      const normalizedTags = newValue.map(tag => TagApi.normalizeTagName(tag));

      // Auto-create any new tags that don't exist yet
      for (const tag of normalizedTags) {
        if (!allTags.includes(tag)) {
          await TagApi.create(tag);
          // Add to local list immediately
          setAllTags(prev => [...prev, tag].sort());
        }
      }

      // Convert array back to comma-separated string
      const tagsString =
        normalizedTags.length > 0 ? normalizedTags.join(',') : null;
      onChange(tagsString);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tags');
      logger.error('Failed to save tags', { error: err });
    }
  };

  const handleInputChange = (_event: unknown, newInputValue: string) => {
    // Real-time kebab-case conversion as user types
    const normalized = TagApi.normalizeTagName(newInputValue);
    return normalized;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Autocomplete
        multiple
        freeSolo
        options={allTags}
        value={selectedTags}
        onChange={handleChange}
        onInputChange={handleInputChange}
        loading={loading}
        fullWidth={fullWidth}
        renderTags={(tagValue: string[], getTagProps) =>
          tagValue.map((option: string, index: number) => (
            <Chip label={option} {...getTagProps({ index })} key={option} />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder="Type to add tags..."
          />
        )}
      />
    </Box>
  );
};
